(ns frontend.view.nev.kyc.process.nationality-editor
  (:require
   [common.country :as country]
   [common.localization :as loc]
   [common.schema.entities.workspace.kyc.kyc-utils :as kyc-utils]
   [common.util :as cu]
   [frontend.view.modals.confirmation.view :as confirmation-modal]
   [frontend.view.modals.dynamic.view :as dynamic-modal]
   [frontend.view.modals.events :as modals]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.editors.labelled-input.view :as labelled-input]))

(defn missing-mandatory-fields
  [{:keys [kyc-process-status nationality-row]}]
  (cond
    (not (:nationality nationality-row))
    [:nationality]

    (and (not= kyc-process-status :kyc-process.status/draft)
         (cu/blank-value? (:id-number nationality-row)))
    [:national-id-number]))

(defn upsert-nationality-row
  [{:keys [rows row-index nationality-row]}]
  (let [updated-rows (if (some? row-index)
                       (assoc (vec rows) row-index nationality-row)
                       (conj (vec rows) nationality-row))]
    (mapv (fn [idx row]
            (assoc row :ord idx))
          (range)
          updated-rows)))

(defn open-nationality-editor!
  [{:keys [lang-kw answers-atom kyc-process-status question-attribute rows row-index
           nationality-row save!]}]
  (let [nationality-ratom (r/atom (or nationality-row {}))
        edit? (some? row-index)
        country-options (->> (country/localized-options lang-kw)
                             (mapv (fn [opt]
                                     {:value (:keyword opt)
                                      :label (:label opt)})))]
    (modals/open-modal!
     dynamic-modal/modal-key
     {:title               (loc/tr (if edit?
                                     :edit-nationality
                                     :add-nationality)
                                   nil
                                   lang-kw)
      :render-fn
      (fn [_modal-state _close-modal!]
        [:div
         {:class "p-3 flex flex-col gap-4"}
         [labelled-input/view
          {:label       (loc/tr :nationality nil lang-kw)
           :editor-type :editor.type/select
           :value       (->> country-options
                             (cu/filter-by :value (:nationality @nationality-ratom))
                             (first))
           :options     country-options
           :on-change   (fn [selected-value _selected-option]
                          (swap! nationality-ratom assoc
                                 :nationality
                                 selected-value))}]

         (when (:nationality @nationality-ratom)
           [labelled-input/view
            {:label     (loc/tr :national-id-number nil lang-kw)
             :data-type :data.type/text-simple
             :value     (:id-number @nationality-ratom)
             :on-change (fn [new-value]
                          (swap! nationality-ratom assoc
                                 :id-number
                                 new-value))
             :on-save!  (fn [new-value]
                          (swap! nationality-ratom assoc
                                 :id-number
                                 new-value))}])])
      :show-button?        true
      :button-text         (loc/tr (if edit? :save :add-nationality) nil lang-kw)
      :custom-validator-fn (fn [_state]
                             (->> (missing-mandatory-fields
                                   {:kyc-process-status kyc-process-status
                                    :nationality-row @nationality-ratom})
                                  (map (fn [translation-key]
                                         (loc/tr translation-key nil lang-kw)))
                                  (seq)))
      :on-submit!          (fn [_]
                             (swap! answers-atom assoc
                                    question-attribute
                                    (upsert-nationality-row
                                     {:rows            rows
                                      :row-index       row-index
                                      :nationality-row @nationality-ratom}))
                             (save!))
      :on-before-close!    (fn [close!]
                             (if (empty? @nationality-ratom)
                               (close!)
                               (modals/open-modal!
                                confirmation-modal/modal-key
                                {:body           (loc/tr :confirm-close-kyc-modal-without-saving nil lang-kw)
                                 :confirm-text   (loc/tr :close-without-saving nil lang-kw)
                                 :confirm-icon   ""
                                 :skip-checkbox? true
                                 :on-confirm     close!})))})))

(defn view
  [{:keys [_kyc-entity-editor _process-id kyc-process _state-atom lang-kw question-attribute
           _question-meta answers-atom mandatory? read-only? save!]
    :as   _args}]
  (let [nationality-keys (kyc-utils/nationality-keys-for question-attribute)
        rows (->> (kyc-utils/nationality-rows @answers-atom nationality-keys)
                  (sort-by :ord)
                  (vec))]
    [:table.zap-table.zap-table--striped.zap-table--has-footer.zap-table--width-auto
     {:style {:width "100%"}}
     [:thead.zap-table__headline
      [:tr.zap-table__row
       [:th.zap-table__headline
        {:class
         (when mandatory?
           "after:ml-0.5 after:text-danger-regular after:content-['*']")}
        (loc/tr :nationality nil lang-kw)]
       [:th.zap-table__headline
        (loc/tr :national-id-number nil lang-kw)]
       [:th.zap-table__headline]]]
     [:tbody
      (->> rows
           (map-indexed
            (fn [idx row]
              [:tr.zap-table__row
               {:key (str "nationality-" idx)}
               [:td.zap-table__cell
                [:span
                 (country/get-label (:nationality row) lang-kw)]]
               [:td.zap-table__cell
                [:span
                 (:id-number row)]]
               [:td.zap-table__cell
                (when-not read-only?
                  [:div.flex.items-center.gap-3
                   [:i.fal.fa-pencil.pointer
                    {:title    (loc/tr :edit-nationality nil lang-kw)
                     :on-click (fn [_]
                                 (open-nationality-editor!
                                  {:lang-kw            lang-kw
                                   :answers-atom       answers-atom
                                   :kyc-process-status (:kyc-process/status kyc-process)
                                   :question-attribute question-attribute
                                   :rows               rows
                                   :row-index          idx
                                   :nationality-row    row
                                   :save!              save!}))}]
                   [:i.fal.fa-trash-alt.pointer
                    {:on-click (fn [_]
                                 (swap! answers-atom assoc
                                        question-attribute
                                        (concat (take idx rows)
                                                (drop (inc idx) rows)))
                                 (save!))}]])]]))
           (doall))]
     (when-not read-only?
       [:tfoot
        [:tr.zap-table__row
         [:td.zap-table__footer-cell
          {:col-span "3"}
          [button/primary-solid
           {:on-click (fn []
                        (open-nationality-editor!
                         {:lang-kw            lang-kw
                          :answers-atom       answers-atom
                          :kyc-process-status (:kyc-process/status kyc-process)
                          :question-attribute question-attribute
                          :rows               rows
                          :save!              save!}))}
           [:i.fal.fa-plus.pr-2]
           (loc/tr :add-nationality nil lang-kw)]]]])]))
